import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { ContactsService } from '@services';
import { formatUAPhone } from '@shared/utils/helpers.util';
import { getError } from '@shared/utils/error.utils';
import { NameErrors, PhoneErrors } from '@shared/errors/error-messages';
import { NotHighlightPristineErrorStateMatcher } from '@shared/classes/not-highlight-pristine-error-state-matcher';
import { AbstractFormContainer } from '@shared/classes/abstract-form-container';
import { AutoCompleteTransformFn, AutocompleteWithControlsConfig } from '@shared/modules/autocomplete-with-controls';
import { CatalogContact, ContactPerson } from '@models/contact.model';
import { Contragent } from '@models/contragent.model';

@Component({
  selector: 'app-private-person-form',
  templateUrl: './private-person-form.component.html',
  styleUrls: ['./private-person-form.component.scss'],
})
export class PrivatePersonFormComponent
  extends AbstractFormContainer
  implements OnInit, OnDestroy {
  protected readonly getError = getError;
  protected readonly phoneErrors = PhoneErrors;
  protected readonly nameErrors = NameErrors;
  @Input() controlName = 'PrivatePersonContact';
  @Input() contactProperty = 'Recipient';
  @Input() templateValidation: boolean;
  @Input() invoiceId: string;
  @Input() IsEnabledCancel = false;
  @Input() isMiddleNameRequired = false;
  @Input() isOrganizationDelegate = false;
  @Input() initialContactValue: ContactPerson;
  @Input() clearOnPhoneChange = true;
  @Input() patchRecipientControlName: string;
  @Input() patchRecipientData: string;
  @Input() disablePhoneInput: boolean;

  @Output() onCancel = new EventEmitter<string>();
  @Output() recipientSelected: EventEmitter<ContactPerson> = new EventEmitter();

  @ViewChild('lastNameInput') lastNameInput: ElementRef;

  matcher = new NotHighlightPristineErrorStateMatcher();
  config: AutocompleteWithControlsConfig = {
    ariaLabel: 'Місто',
    placeholder: 'Місто',
    arrow: false,
  };
  privatePersonFormGroup: UntypedFormGroup;
  contactOptions: ContactPerson[] = [];
  catalogOptions: CatalogContact[] = [];
  showNotFoundContacts = false;
  lastNameAutocompleteConfig: AutocompleteWithControlsConfig = {
    ariaLabel: 'Прізвище',
    placeholder: 'Прізвище',
    arrow: true,
  };

  constructor(
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private contactService: ContactsService,
  ) {
    super();
  }

  private _subscriptions: Subscription[] = [];

  get subscriptions(): any {
    return this._subscriptions;
  }

  set subscriptions(subscription: any) {
    this._subscriptions.push(subscription);
  }

  get phoneControl(): AbstractControl {
    return this.privatePersonFormGroup.get('phone');
  }

  get lastNameControl(): AbstractControl {
    return this.privatePersonFormGroup.get('lastName');
  }

  get firstNameControl(): AbstractControl {
    return this.privatePersonFormGroup.get('firstName');
  }

  get middleNameControl(): AbstractControl {
    return this.privatePersonFormGroup.get('middleName');
  }

  get recipientTypeControl(): AbstractControl {
    return this.form.get('RecipientCounterpartyType');
  }

  get PrivatePersonContact(): AbstractControl {
    return this.form.get(this.controlName);
  }

  get patchRecipientControl(): AbstractControl {
    return this.form.get(this.patchRecipientControlName);
  }

  get recipientType(): AbstractControl {
    return this.form.get('recipientType');
  }

  get privatePerson(): boolean {
    return this.recipientType.value === 'PrivatePerson';
  }

  transformFn: AutoCompleteTransformFn<ContactPerson> = (
    value: string | ContactPerson,
  ) =>
    typeof value === 'string'
      ? value
      : `${value.Description || ''} ${formatUAPhone(value.Phones || '')}`;

  ngOnInit() {
    super.ngOnInit();
    this.initCatalogContactSearchSubscription();
    this.initStatusSubscriptions();
    this.initChangesSubscription();
    this.initRecipientCounterpartyTypeSubscription();

    if (this.PrivatePersonContact) {
      this.subscriptions = this.PrivatePersonContact.valueChanges.subscribe(
        () => {
          if (
            this.PrivatePersonContact.get('firstName') &&
            this.PrivatePersonContact.get('firstName').status === 'DISABLED'
          ) {
            this.PrivatePersonContact.get('firstName').markAsPristine();
            this.PrivatePersonContact.get('firstName').markAsUntouched();
            this.PrivatePersonContact.get('firstName').updateValueAndValidity({
              onlySelf: true,
            });
          }
          if (
            this.PrivatePersonContact.get('lastName') &&
            this.PrivatePersonContact.get('lastName').status === 'DISABLED'
          ) {
            this.PrivatePersonContact.get('lastName').markAsPristine();
            this.PrivatePersonContact.get('lastName').markAsUntouched();
            this.PrivatePersonContact.get('lastName').updateValueAndValidity({
              onlySelf: true,
            });
          }
        },
      );
    }

    if (this.templateValidation) {
      Object.keys(this.PrivatePersonContact['controls']).forEach((key) => {
        this.form.controls[this.controlName]['controls'][key].clearValidators();
      });
    }
  }

  initChangesSubscription(): void {
    this.subscriptions = this.privatePersonFormGroup.valueChanges.subscribe(
      () => this.form.removeControl('Recipient'),
    );
  }

  initRecipientCounterpartyTypeSubscription(): void {
    if (!this.recipientTypeControl) {
      return;
    }

    this.subscriptions = this.recipientTypeControl.valueChanges.subscribe(
      (type) => {
        if (type === 'OrganizationDelegate') {
          this.privatePersonFormGroup.patchValue({
            lastName: 'Представник',
            firstName: 'Представник',
            middleName: 'Представник',
          });
          this.lastNameControl.disable();
          this.firstNameControl.disable();
          this.middleNameControl.disable();
        }
        if (
          this.form.value.RecipientCounterpartyType ===
          'OrganizationDelegate' &&
          type !== 'OrganizationDelegate'
        ) {
          this.privatePersonFormGroup.patchValue({
            lastName: '',
            firstName: '',
            middleName: '',
          });
          if (this.phoneControl.valid) {
            this.lastNameControl.enable();
          }
        }
      },
    );
  }

  addFormControls(): void {
    this.privatePersonFormGroup = this.fb.group({
      phone: [
        this.initialContactValue ? this.initialContactValue.Phones : '',
        Validators.compose([Validators.required, Validators.minLength(12)]),
      ],
      lastName: [
        {
          value: this.initialContactValue
            ? this.initialContactValue.LastName
            : '',
          disabled: this.clearOnPhoneChange,
        },
        Validators.compose([Validators.required]),
      ],
      firstName: [
        {
          value: this.initialContactValue
            ? this.initialContactValue.FirstName
            : '',
          disabled: this.clearOnPhoneChange,
        },
        Validators.compose([Validators.required]),
      ],
      middleName: [
        {
          value: this.initialContactValue
            ? this.initialContactValue.MiddleName
            : '',
          disabled: this.clearOnPhoneChange,
        },
        Validators.compose([
          this.isMiddleNameRequired ? Validators.required : null,
        ]),
      ],
      ref: [{ value: '' }],
      counterRef: [{ value: '' }],
    });
    this.form.setControl(this.controlName, this.privatePersonFormGroup);
    this.patchData();
  }

  patchData(): void {
    if (!(this.patchRecipientControlName && this.patchRecipientData)) {
      return;
    }

    this.PrivatePersonContact.patchValue({
      lastName:
        this.patchRecipientControl.value[this.patchRecipientData]?.split(
          ' ',
        )[0] || '',
      firstName:
        this.patchRecipientControl.value[this.patchRecipientData]?.split(
          ' ',
        )[1] || '',
      middleName:
        this.patchRecipientControl.value[this.patchRecipientData]?.split(
          ' ',
        )[2] || '',
      phone: this.patchRecipientControl.value.PhoneRecipient || '',
    });
  }

  initCatalogContactSearchSubscription(): void {
    this.subscriptions = this.lastNameControl.valueChanges
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((lastName) => {
        this.showNotFoundContacts = false;
        if (
          typeof lastName === 'string' &&
          lastName.length >= 3 &&
          !this.isOrganizationDelegate
        ) {
          // this.firstNameControl.enable();
          // this.middleNameControl.enable();
          // if (!this.cd['destroyed']) {
          //   this.cd.detectChanges();
          // }
          if (this.phoneControl.valid && this.privatePerson) {
            this.searchRecipientContacts(lastName);
          }
        } else {
          this.contactOptions = [];
          // if (!this.cd['destroyed']) {
          //   this.cd.detectChanges();
          // }
        }
        // if (!this.cd['destroyed']) {
        //   this.cd.detectChanges();
        // }
      });
  }

  initStatusSubscriptions(): void {
    this.subscriptions = this.phoneControl.statusChanges.subscribe((status) => {
      // if (status === 'VALID' && !this.isOrganizationDelegate) {
      //   this.lastNameControl.enable();
      //   if (!this.templateValidation) {
      //     this.lastNameInput.nativeElement.focus();
      //   }
      // } else {
      //   this.contactOptions = [];
      //   this.catalogOptions = [];
      //   if (!this.isOrganizationDelegate && this.clearOnPhoneChange) {
      //     this.privatePersonFormGroup.patchValue({
      //       lastName: '',
      //       firstName: '',
      //       middleName: '',
      //     });
      //   }
      //   this.lastNameControl.disable();
      //   this.firstNameControl.disable();
      //   this.middleNameControl.disable();
      // }
    });
  }

  searchRecipientContacts(query: string): void {
    const observables: Array<
      Observable<CatalogContact[]> | Observable<ContactPerson[]>
    > = [
      this.contactService.searchContactsInCatalog(
        query,
        this.phoneControl.value,
      ),
    ];
    // if (!this.form.get('NewContactOrganization')) {
    //   observables.push(this.contactService.searchRecipientContacts(query));
    // }
    forkJoin(...observables).subscribe((data: any) => {
      this.catalogOptions = data[0];
      this.contactOptions = data[1]
        ? data[1].filter(
          (contact) => contact.Phones === this.phoneControl.value,
        )
        : [];
    });
  }

  setRecipient(
    contact: ContactPerson,
    isFromCatalog: boolean,
    contragent: Contragent,
  ) {
    this.privatePersonFormGroup.patchValue({
      lastName: contact.LastName ? contact.LastName : '',
      firstName: contact.FirstName ? contact.FirstName : '',
      middleName: contact.MiddleName ? contact.MiddleName : '',
    });

    this.form.get('Contragent')
      ? this.form
        .get('Contragent')
        .reset({ value: contragent, disabled: isFromCatalog })
      : this.form.setControl(
        'Contragent',
        new UntypedFormControl(
          {
            value: contragent,
            disabled: isFromCatalog,
          },
          Validators.required,
        ),
      );
    this.setOrganizationDisplay(contragent);
    this.setRecipientCounterpartyType(contragent);
    if (!isFromCatalog) {
      this.form.setControl(
        'RecipientSearchObject',
        new UntypedFormControl(contact, Validators.required),
      );
    }
    this.form.setControl(
      this.contactProperty,
      new UntypedFormControl(contact, Validators.required),
    );
    this.recipientSelected.next(contact);
    if (!this.cd['destroyed']) {
      this.cd.detectChanges();
    }
  }

  setOrganizationDisplay(contragent: Contragent) {
    if (this.form.get('NewContactOrganization')) {
      this.form.get('NewContactOrganization').patchValue({
        edrpou: contragent ? contragent.EDRPOU : '',
        companyName: contragent.Description
          ? contragent.Description
          : contragent.Description,
        orgType: contragent.OwnershipFormDescription
          ? contragent.OwnershipFormDescription
          : '',
      });
    }
  }

  setRecipientCounterpartyType(contragent: Contragent) {
    const recipientCounterpartyType =
      contragent.CounterpartyType === 'PrivatePerson'
        ? 'PrivatePerson'
        : 'Organization';
    if (this.form.get('RecipientCounterpartyType')) {
      this.form
        .get('RecipientCounterpartyType')
        .setValue(recipientCounterpartyType);
    } else if (this.form.get('recipientType')) {
      this.form.get('recipientType').setValue(recipientCounterpartyType);
    }
  }

  setContactFromCatalog(event): void {
    const catalogContact: CatalogContact = event.option.value;

    this.contactService
      .getContragentByDescription(
        catalogContact.EDRPOU,
        catalogContact.Description,
        catalogContact.OwnershipForm,
      )
      .pipe(
        switchMap((contr: Contragent | undefined) =>
          !contr
            ? this.contactService.getByEdrpouBot(catalogContact.EDRPOU)
            : of(contr),
        ),
        // tap(contr => {
        //   contragent = contr;
        //   catalogContact.RefCounterparty = contragent.Ref;
        // }),
        // switchMap((contr: Contragent) => {
        //   contragent = contr;
        //   catalogContact.RefCounterparty = contragent.Ref;
        //   return this.contactService.createContactFromCatalog(catalogContact, this.phoneControl.value);
        // }),
        // switchMap((contact: SavedCatalogContact) => this.contactService.getByRef(contact.Ref)),
        tap(() => {
          if (this.form?.get('Contragent')?.value?.Ref) {
            const ref = this.form.get('Contragent').value.Ref;
            const newContact = {
              ...catalogContact,
              CounterpartyRef: ref,
              RefCounterparty: ref,
            };
            // return this.contactService.update(newContact).pipe(tap(c => c.CounterpartyRef = ref));
          }
        }),
        catchError((err) => of(err)),
      )
      .subscribe((contragent) =>
        this.setRecipient(catalogContact, true, contragent),
      );
  }

  clear(): void {
    this.form.removeControl(this.contactProperty);
    this.privatePersonFormGroup.patchValue({
      phone: '',
      lastName: '',
      firstName: '',
      middleName: '',
    });
  }

  onChangePhone(event) {
    // event.preventDefault();
    // const phone = event.srcElement.value;
    // if (phone.length === 19) {
    //   this.lastNameInput.nativeElement.focus();
    // }
  }

  cancel(): void {
    this.onCancel.next('select');
  }

  ngOnDestroy(): void {
    if (this.clearOnPhoneChange) {
      this.form.removeControl(this.controlName);
    }

    this.form.removeControl('NewSenderPrivateContact');

    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
